import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Login,
  Logout,
  AccessTime,
  ExitToApp,
  Timelapse,
  CheckCircle,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import configService from "../services/config";

// Data ka "date" field UTC me pichle din 18:30 pe aata hai (IST offset ki wajah se).
// Isliye compare karne ke liye IST me convert karke date-string nikalte hain.
const toISTDateString = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(d.getTime() + istOffsetMs);
  return istDate.toISOString().split("T")[0];
};

const formatISTTime = (dateStr) => {
  if (!dateStr) return "--:--";
  return new Date(dateStr).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { userId, company_id } = useSelector((state) => state.globalVariables);

  const [apiData, setApiData] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // aaj ka attendance record
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(true);

  const isOut = apiData.in && !apiData.out;

  // INIT
  const initDashboard = async () => {
    setLoading(true);
    try {
      const statusRes = await configService.getTodayStatus({
        employee_id: userId,
        company_id,
      });

      if (statusRes?.status) setApiData(statusRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // get-attendance-by-date-range se sirf aaj (present day) ka record nikalna
  const fetchTodayAttendance = async () => {
    setAttendanceLoading(true);
    try {
      const todayStr = new Date().toISOString().split("T")[0];

      const res = await configService.getAttendanceByDateRange({
        employee_id: userId,
        company_id,
        start_date: todayStr,
        end_date: todayStr,
      });

      if (res?.status && Array.isArray(res.data)) {
        const todayIST = toISTDateString(new Date().toISOString());
        const record = res.data.find(
          (item) => toISTDateString(item.date) === todayIST
        );
        setTodayAttendance(record || null);
      } else {
        setTodayAttendance(null);
      }
    } catch (err) {
      console.error(err);
      setTodayAttendance(null);
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      initDashboard();
      fetchTodayAttendance();
    }
  }, [userId]);

  // CLOCK
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // live duration calculate karna (agar punch_out nahi hai to current time tak)
  const computeLiveDuration = (record) => {
    if (!record || !record.punch_in) return "--";
    if (record.duration) return record.duration;
    const start = new Date(record.punch_in);
    const end = record.punch_out ? new Date(record.punch_out) : currentTime;
    const diffMs = Math.max(end - start, 0);
    const hrs = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    return `${hrs} hr ${mins} min`;
  };

  const attendanceCards = [
    {
      label: "In Time",
      value: todayAttendance ? formatISTTime(todayAttendance.punch_in) : "--:--",
      icon: <AccessTime sx={{ fontSize: 22 }} />,
      color: "#16a34a",
    },
    {
      label: "Out Time",
      value: todayAttendance?.punch_out ? formatISTTime(todayAttendance.punch_out) : "Not Punched Out",
      icon: <ExitToApp sx={{ fontSize: 22 }} />,
      color: "#e11d48",
    },
    {
      label: "Status",
      value: todayAttendance ? todayAttendance.status : "Not Marked",
      icon: <CheckCircle sx={{ fontSize: 22 }} />,
      color: "#0288d1",
    },
    {
      label: "Duration",
      value: computeLiveDuration(todayAttendance),
      icon: <Timelapse sx={{ fontSize: 22 }} />,
      color: "#ed6c02",
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      <Card sx={{ bgcolor: "#ffffff", color: "#1e293b", p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>

        {/* TOP CLOCK & PUNCH SYSTEM */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 3, sm: 4 }}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%" }}
        >
          <Box
            sx={{
              width: 130,
              height: 130,
              borderRadius: "50%",
              border: "4px solid #1976d2",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "#f8fafc",
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: "bold" }}>
              CURRENT TIME
            </Typography>
            <Typography sx={{ fontFamily: "monospace", color: "#1e293b", fontWeight: "bold", fontSize: "1.1rem" }}>
              {currentTime.toLocaleTimeString()}
            </Typography>
          </Box>

          <Button
            onClick={() => navigate(isOut ? "/dashboard/punch-out" : "/dashboard/punch-in")}
            variant="contained"
            sx={{
              width: 130,
              height: 130,
              borderRadius: "50%",
              bgcolor: isOut ? "#e11d48" : "#16a34a",
              color: "#fff",
              flexDirection: "column",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              alignSelf: "center",
              "&:hover": { bgcolor: isOut ? "#be123c" : "#15803d" },
            }}
          >
            {isOut ? <Logout sx={{ fontSize: 30, mb: 0.5 }} /> : <Login sx={{ fontSize: 30, mb: 0.5 }} />}
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              {isOut ? "OUT" : "IN"}
            </Typography>
          </Button>
        </Stack>

        <Divider sx={{ my: 4, borderColor: "#e2e8f0" }} />

        {/* TODAY'S ATTENDANCE (In Time / Out Time / Status / Duration) */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#0f172a" }}>
          Today's Attendance
        </Typography>

        {attendanceLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2.5,
            }}
          >
            {attendanceCards.map((card) => (
              <Card
                key={card.label}
                sx={{
                  bgcolor: "#ffffff",
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ height: 4, bgcolor: card.color }} />
                <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: `${card.color}1a`,
                      color: card.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                      {card.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#0f172a",
                        fontWeight: "700",
                        fontSize: "0.95rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default Dashboard;